import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UtensilsCrossed, Dumbbell, Waves, Music, Drama, Baby, Trees, PartyPopper, Sparkles, CalendarX, CalendarCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { t, TOPICS, slotTime } from "@/i18n";

const ICONS = { UtensilsCrossed, Dumbbell, Waves, Music, Drama, Baby, Trees, PartyPopper, Sparkles };

export const BookingForm = ({ lang, data, choice, submitting, onChange, onNext, onBack }) => {
  const L = t[lang];
  const [errors, setErrors] = useState({});
  const [existing, setExisting] = useState([]);
  const [cancelling, setCancelling] = useState(null);

  const set = (k, v) => onChange({ ...data, [k]: v });

  const checkExisting = async () => {
    const email = (data.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setExisting([]);
      return;
    }
    try {
      const { data: found } = await api.get(`/bookings/by-email?email=${encodeURIComponent(email)}`);
      setExisting(found || []);
    } catch (e) {
      setExisting([]);
    }
  };

  const cancelOne = async (id) => {
    setCancelling(id);
    try {
      await api.post(`/bookings/${id}/cancel`, { email: data.email.trim() });
      toast.success(L.cancelled);
      setExisting((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      toast.error(L.errorGeneric);
    } finally {
      setCancelling(null);
    }
  };

  const toggleTopic = (key) => {
    const has = data.topics.includes(key);
    set("topics", has ? data.topics.filter((x) => x !== key) : [...data.topics, key]);
  };

  const validate = () => {
    const e = {};
    if (!data.name?.trim()) e.name = L.required;
    if (!data.email?.trim()) e.email = L.required;
    if (!data.phone?.trim()) e.phone = L.required;
    if (!data.rgpd_consent) e.rgpd = L.rgpdRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35 }}
      className="w-full max-w-xl mx-auto"
      data-testid="booking-form"
    >
      <p className="text-sm font-medium text-coastal-accent tracking-wide uppercase mb-2">
        {L.brand}
      </p>
      <h2 className="font-heading text-3xl sm:text-4xl text-coastal-text mb-8">
        {L.stepDetails}
      </h2>
      {choice && (
        <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-coastal-primary/10 text-coastal-primary text-sm font-medium" data-testid="chosen-slot-summary">
          <CalendarCheck size={16} /> {choice.date} · {slotTime(choice.slot)} · {data.party_size || 1} pers.
        </div>
      )}

      <div className="space-y-6">
        <div>
          <Label className="text-coastal-text font-medium">{L.name}</Label>
          <Input
            data-testid="input-name"
            value={data.name}
            onChange={(e) => set("name", e.target.value)}
            className="mt-1.5 rounded-lg border-coastal-secondary focus:ring-2 focus:ring-coastal-primary"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="error-name">{errors.name}</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-coastal-text font-medium">{L.email}</Label>
            <Input
              data-testid="input-email"
              type="email"
              value={data.email}
              onChange={(e) => set("email", e.target.value)}
              onBlur={checkExisting}
              className="mt-1.5 rounded-lg border-coastal-secondary focus:ring-2 focus:ring-coastal-primary"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="error-email">{errors.email}</p>}
          </div>
          <div>
            <Label className="text-coastal-text font-medium">{L.phone}</Label>
            <Input
              data-testid="input-phone"
              value={data.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="mt-1.5 rounded-lg border-coastal-secondary focus:ring-2 focus:ring-coastal-primary"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1" data-testid="error-phone">{errors.phone}</p>}
          </div>
        </div>

        {existing.length > 0 && (
          <div className="rounded-lg border border-coastal-accent/40 bg-coastal-accent/5 p-4" data-testid="existing-bookings">
            <p className="font-medium text-coastal-text flex items-center gap-2">
              <CalendarX size={16} className="text-coastal-accent" /> {L.existingTitle}
            </p>
            <p className="text-xs text-coastal-muted mt-1 mb-3">{L.existingDesc}</p>
            <div className="space-y-2">
              {existing.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-white rounded-lg border border-coastal-secondary px-3 py-2">
                  <span className="text-sm text-coastal-text">
                    {b.date} · {slotTime(b.slot)} · {b.party_size} pers.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`cancel-own-${b.id}`}
                    disabled={cancelling === b.id}
                    onClick={() => cancelOne(b.id)}
                    className="rounded-full border-red-300 text-red-600 hover:bg-red-50 h-8"
                  >
                    {cancelling === b.id ? L.cancelling : L.cancelBooking}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="text-coastal-text font-medium block mb-3">{L.interests}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="topics-grid">
            {TOPICS.map((topic) => {
              const Icon = ICONS[topic.icon];
              const active = data.topics.includes(topic.key);
              return (
                <button
                  key={topic.key}
                  type="button"
                  data-testid={`topic-${topic.key}`}
                  onClick={() => toggleTopic(topic.key)}
                  className={`flex items-center gap-2 px-3 py-3 rounded-lg border text-left text-sm transition-colors duration-200 ${
                    active
                      ? "bg-coastal-primary/10 border-coastal-primary text-coastal-primary"
                      : "bg-white border-coastal-secondary text-coastal-muted hover:border-coastal-primary"
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  <span className="leading-tight">{topic[lang]}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-coastal-text font-medium">{L.customQuestion}</Label>
          <Textarea
            data-testid="input-custom-topic"
            value={data.custom_topic}
            onChange={(e) => set("custom_topic", e.target.value)}
            placeholder={L.customPlaceholder}
            className="mt-1.5 rounded-lg border-coastal-secondary focus:ring-2 focus:ring-coastal-primary min-h-[90px]"
          />
        </div>

        <div className="flex items-start gap-3 bg-coastal-secondary/20 p-4 rounded-lg">
          <Checkbox
            id="rgpd"
            data-testid="checkbox-rgpd"
            checked={data.rgpd_consent}
            onCheckedChange={(v) => set("rgpd_consent", !!v)}
            className="mt-0.5"
          />
          <label htmlFor="rgpd" className="text-sm text-coastal-muted leading-relaxed cursor-pointer">
            {L.rgpd}
          </label>
        </div>
        {errors.rgpd && <p className="text-red-500 text-xs" data-testid="error-rgpd">{errors.rgpd}</p>}
      </div>

      <div className="flex gap-3 mt-8">
        <Button
          variant="outline"
          data-testid="form-back-btn"
          onClick={onBack}
          className="rounded-full border-coastal-secondary text-coastal-muted hover:text-coastal-text"
        >
          {L.back}
        </Button>
        <Button
          data-testid="form-next-btn"
          onClick={handleNext}
          disabled={submitting}
          className="rounded-full bg-coastal-accent hover:bg-coastal-accent/90 text-white px-8 flex-1 transition-transform duration-200 hover:-translate-y-0.5"
        >
          {submitting ? L.submitting : L.confirmBooking}
        </Button>
      </div>
    </motion.div>
  );
};

export default BookingForm;
